import {
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle
} from 'react'
import IMask, { InputMask } from 'imask'
import Input from '@/components/common/Input/Input'

interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value?: string
  onChange?: (value: string) => void
  label?: string
  error?: string
  className?: string
  disabled?: boolean
  placeholder?: string
  suffix?: React.ReactNode
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({
    value,
    onChange,
    ...props
  }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const inputMask = useRef<InputMask>(undefined)
    useImperativeHandle(ref, () => inputRef.current!, [])

    useEffect(() => {
      if (!inputRef.current) return

      inputMask.current = IMask(
        inputRef.current,
        {
          mask: '0000-00-00',
          lazy: true,
        }
      )

      if (value !== undefined) {
        inputMask.current.value = value.toString()
      }

      inputMask.current.on("complete", () => {
        const maskValue = inputMask.current?.value
        onChange?.(maskValue || '')
      })

      /* This for some reason is triggered twice */
      if (props.defaultValue) {
        onChange?.(props.defaultValue.toString())
      }

      return () => {
        inputMask.current?.destroy()
      }
    }, [])

    useEffect(() => {
      if (inputMask.current && value !== undefined) {
        inputMask.current.value = value.toString()
      }
    }, [value])

    useEffect(() => {
      if (inputMask.current && props.defaultValue) {
        inputMask.current.updateValue()
      }
    }, [props.defaultValue])

    return (
      <Input
        ref={inputRef}
        placeholder="YYYY-MM-DD"
        error={props.error}
        {...props}
      />
    )
  }
)

DateInput.displayName = 'DateInput'

export default DateInput